import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../utils/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const DEFAULT_PREFERENCES = {
  vet_portal: true,
  woocommerce: true,
  manual_pet: true,
  ai_receptionist: true,
  pet_collected: true,
  ashes_delivery: true
};

const NOTIFICATION_TYPES = [
  { key: 'vet_portal', label: 'Vet Portal Submissions', description: 'New pets submitted by vet clinics' },
  { key: 'woocommerce', label: 'Online Orders', description: 'WooCommerce website orders' },
  { key: 'manual_pet', label: 'Manual Pet Records', description: 'Pets added manually by staff' },
  { key: 'ai_receptionist', label: 'AI Receptionist Bookings', description: 'Bookings from the AI phone receptionist' },
  { key: 'pet_collected', label: 'Pet Collected by Driver', description: 'When a pet is picked up by the driver' },
  { key: 'ashes_delivery', label: 'Ashes Delivery Scheduled', description: 'When an ashes delivery appointment is booked' }
];

const NotificationSettingsModal = ({ isOpen, onClose, userEmail }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(false);
  const [swError, setSwError] = useState(null);

  // Check support and register SW
  useEffect(() => {
    if (!isOpen) return;

    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);

    const pwa = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsPWA(pwa);

    if (supported) {
      setPermission(Notification.permission);
      navigator.serviceWorker.register('/sw.js')
        .then(reg => {
          setRegistration(reg);
          return reg.pushManager.getSubscription();
        })
        .then(sub => {
          setIsSubscribed(!!sub);
        })
        .catch(err => {
          console.error('SW registration failed:', err);
          setSwError(err.message);
        });
    }
  }, [isOpen]);

  // Load saved preferences
  useEffect(() => {
    if (!isOpen || !userEmail) return;
    
    fetch(`${API_URL}/api/push/preferences?email=${encodeURIComponent(userEmail)}`)
      .then(res => res.json())
      .then(data => {
        if (data.preferences) {
          setPreferences(prev => ({ ...prev, ...data.preferences }));
        }
      })
      .catch(() => {});
  }, [isOpen, userEmail]);

  const handleEnableNotifications = useCallback(async () => {
    setLoading(true);
    try {
      if (!registration) {
        showToast('Service worker not ready. Please reload the page and try again.', 'error');
        setLoading(false);
        return;
      }

      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'denied') {
        showToast('Notifications were blocked. Please enable them in your browser/phone settings.', 'error');
        setLoading(false);
        return;
      }

      if (result !== 'granted') {
        showToast('Notification permission was dismissed. Please try again.', 'error');
        setLoading(false);
        return;
      }

      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        showToast('Push configuration error. Contact admin.', 'error');
        setLoading(false);
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      const subJson = subscription.toJSON();
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          user_email: userEmail || null,
          preferences: preferences
        })
      });

      if (response.ok) {
        setIsSubscribed(true);
        showToast('Push notifications enabled!', 'success');
      } else {
        showToast('Failed to save subscription. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Push subscription failed:', err);
      showToast(`Subscription failed: ${err.message}`, 'error');
    }
    setLoading(false);
  }, [registration, userEmail, preferences]);

  const handleDisableNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const subscription = await registration?.pushManager?.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint })
        });
      }
      setIsSubscribed(false);
      showToast('Push notifications disabled.', 'success');
    } catch (err) {
      console.error('Unsubscribe failed:', err);
      showToast(`Failed to disable: ${err.message}`, 'error');
    }
    setLoading(false);
  }, [registration]);

  const handleSavePreferences = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/push/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          preferences: preferences
        })
      });
      if (response.ok) {
        showToast('Notification preferences saved!', 'success');
      }
    } catch (err) {
      showToast('Failed to save preferences.', 'error');
    }
    setLoading(false);
  }, [userEmail, preferences]);

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="notification-settings-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Notification Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Support Check */}
          {!isSupported ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800">Push notifications are not supported in this browser.</p>
              <p className="text-xs text-red-600 mt-1">Please use a modern browser (Chrome, Safari 16.4+, Firefox) or add this site to your home screen on iPhone.</p>
            </div>
          ) : (
            <>
              {/* PWA Hint for iOS */}
              {!isPWA && /iPhone|iPad/.test(navigator.userAgent) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-800">iPhone Tip</p>
                  <p className="text-xs text-blue-600 mt-1">For push notifications to work on iPhone, the CRM must be added to your Home Screen. Tap the Share button → "Add to Home Screen".</p>
                </div>
              )}

              {/* Permission Denied Warning */}
              {permission === 'denied' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-amber-800">Notifications Blocked</p>
                  <p className="text-xs text-amber-600 mt-1">You previously blocked notifications. To re-enable, go to your browser settings → Site Settings → Notifications → Allow for this site.</p>
                </div>
              )}

              {/* SW Error */}
              {swError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-600">Service worker error: {swError}. Try reloading the page.</p>
                </div>
              )}

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Push Notifications</p>
                  <p className="text-xs text-gray-500">Receive alerts when new pets are added</p>
                </div>
                {isSubscribed ? (
                  <button
                    onClick={handleDisableNotifications}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 transition disabled:opacity-50"
                    data-testid="disable-notifications-btn"
                  >
                    {loading ? 'Disabling...' : 'Disable'}
                  </button>
                ) : (
                  <button
                    onClick={handleEnableNotifications}
                    disabled={loading || permission === 'denied'}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 transition disabled:opacity-50"
                    data-testid="enable-notifications-btn"
                  >
                    {loading ? 'Enabling...' : 'Enable'}
                  </button>
                )}
              </div>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* Notification Type Preferences */}
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-3">What to notify me about</p>
                <div className="space-y-3">
                  {NOTIFICATION_TYPES.map(({ key, label, description }) => (
                    <label key={key} className="flex items-start space-x-3 cursor-pointer group" data-testid={`pref-${key}`}>
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          checked={preferences[key]}
                          onChange={() => togglePreference(key)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">{label}</p>
                        <p className="text-xs text-gray-500">{description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Save Preferences Button */}
              {isSubscribed && (
                <button
                  onClick={handleSavePreferences}
                  disabled={loading}
                  className="w-full py-2.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
                  data-testid="save-preferences-btn"
                >
                  {loading ? 'Saving...' : 'Save Preferences'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
